<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { ArrowRight } from 'lucide-vue-next'
import { appLoginUrl } from '@/config'
import PageHero from '@/components/marketing/PageHero.vue'
import { useSeo } from '@/composables/useSeo'
import { useJsonLd } from '@/composables/useJsonLd'
import { INPUTS_TEXT, imageModelBySlug, otherModelsByTier, secondsText, type ImageModelQuality } from '@/data/imageModels'
import { usePricingStore } from '@/stores/pricing'

// Vite-ssg only enumerates real slugs, so during prerender the model is always
// defined; on the client an unknown slug falls through to NotFound (as UseCaseView).
const route = useRoute()
const router = useRouter()
const SITE_URL = 'https://open-muse.com'
const pricingStore = usePricingStore()

const model = computed(() => imageModelBySlug(String(route.params.slug)))

onMounted(() => {
  if (!model.value) router.replace({ name: 'NotFound' })
  // Prices arrive at runtime only, so a prerendered page never carries a stale number.
  void pricingStore.ensureLoaded()
})

const m = model.value
if (m) {
  useSeo({ title: `${m.name} image model`, description: m.summary })
  const pageUrl = `${SITE_URL}/models/${m.slug}`
  useJsonLd([
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: `${m.name} image model`,
      description: m.summary,
      url: pageUrl,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: m.name, item: pageUrl },
      ],
    },
  ])
}

const chipLabel = (q: ImageModelQuality) => (q.label ? `${q.label}: ${q.tier}` : q.tier)

// Live token prices keyed `${modelId}|${quality}` lowercased; empty while loading or failed.
const costByKey = computed(() => new Map(
  (pricingStore.pricing?.imageModelCosts ?? []).map((r) => [`${r.modelId}|${r.quality}`.toLowerCase(), r.costPerImage]),
))

// One row per quality with its live price (null until the pricing feed has it).
const qualityRows = computed(() => {
  const mod = model.value
  if (!mod) return []
  return mod.qualities.map((q) => ({ q, cost: costByKey.value.get(`${mod.modelId}|${q.quality}`.toLowerCase()) ?? null }))
})

const related = computed(() => (model.value ? otherModelsByTier(model.value) : []))
const ctaHref = appLoginUrl({ mode: 'signup' })

const rowClass = 'grid gap-1 border-t border-surface-800 py-4 sm:grid-cols-[10rem_1fr] sm:gap-6'
</script>

<template>
  <div v-if="model" class="min-h-screen bg-surface-950">
    <PageHero>
      <template #eyebrow>Image model</template>
      <template #title>{{ model.name }}</template>
      <template #description>{{ model.goodFor }}</template>
      <template #meta>
        <span
          v-for="q in model.qualities"
          :key="q.quality"
          class="text-xs font-medium text-surface-300 [&:not(:last-child)]:after:content-['·'] [&:not(:last-child)]:after:ml-2"
          data-model-tier
        >{{ chipLabel(q) }}</span>
      </template>
    </PageHero>

    <section class="px-6 pb-14 sm:px-8 lg:px-10">
      <div class="mx-auto max-w-3xl">
        <dl class="border-b border-surface-800" data-model-facts>
          <div :class="rowClass">
            <dt class="text-sm font-semibold text-surface-100">Speed</dt>
            <dd class="space-y-1 text-sm text-surface-300">
              <p v-for="q in model.qualities" :key="q.quality">
                <span v-if="q.label" class="text-surface-400">{{ q.label }}: </span>About {{ secondsText(q) }} seconds
              </p>
            </dd>
          </div>
          <div :class="rowClass">
            <dt class="text-sm font-semibold text-surface-100">What it takes</dt>
            <dd class="text-sm text-surface-300">{{ INPUTS_TEXT[model.inputs] }}</dd>
          </div>
          <div :class="rowClass">
            <dt class="text-sm font-semibold text-surface-100">Cost</dt>
            <dd class="space-y-1 text-sm text-surface-300">
              <p v-for="{ q, cost } in qualityRows" :key="q.quality">
                <span v-if="q.label" class="text-surface-400">{{ q.label }}: </span>
                <span v-if="cost !== null" class="font-semibold text-brass-400">{{ cost }} {{ cost === 1 ? 'token' : 'tokens' }} a picture</span>
                <RouterLink v-else to="/pricing" class="text-primary-300 underline-offset-2 hover:underline">See current prices</RouterLink>
              </p>
            </dd>
          </div>
        </dl>
      </div>
    </section>

    <section class="px-6 pb-14 sm:px-8 lg:px-10">
      <div class="mx-auto grid max-w-3xl gap-8 sm:grid-cols-2">
        <div>
          <h2 class="mb-3 text-xl font-bold text-surface-50">Good at</h2>
          <ul class="list-disc space-y-2 pl-5 text-sm leading-relaxed text-surface-300 marker:text-surface-500">
            <li v-for="s in model.strengths" :key="s">{{ s }}</li>
          </ul>
        </div>
        <div>
          <h2 class="mb-3 text-xl font-bold text-surface-50">Watch for</h2>
          <ul class="list-disc space-y-2 pl-5 text-sm leading-relaxed text-surface-300 marker:text-surface-500">
            <li v-for="l in model.limits" :key="l">{{ l }}</li>
          </ul>
        </div>
      </div>
    </section>

    <section v-if="model.samples.length > 0" class="px-6 pb-14 sm:px-8 lg:px-10">
      <div class="mx-auto max-w-3xl">
        <h2 class="mb-4 text-xl font-bold text-surface-50">Samples</h2>
        <div :class="['grid grid-cols-2 gap-3', model.samples.length === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2']">
          <figure v-for="s in model.samples" :key="s.src" class="overflow-hidden rounded-card border border-surface-800 bg-surface-900">
            <div class="aspect-[3/4]">
              <img :src="s.src" :alt="s.alt" loading="lazy" decoding="async" class="h-full w-full object-cover" />
            </div>
            <figcaption v-if="s.prompt" class="p-2 text-xs text-surface-400">{{ s.prompt }}</figcaption>
          </figure>
        </div>
      </div>
    </section>

    <section v-if="related.length > 0" class="border-t border-surface-800 bg-surface-900 px-6 py-12 sm:px-8 lg:px-10">
      <div class="mx-auto max-w-3xl space-y-6">
        <div v-for="g in related" :key="g.tier">
          <h2 class="mb-3 text-lg font-bold text-surface-50">Other models in {{ g.tier }}</h2>
          <ul class="flex flex-wrap gap-2">
            <li v-for="o in g.models" :key="o.slug">
              <RouterLink
                :to="`/models/${o.slug}`"
                :data-related-model="o.slug"
                class="inline-flex min-h-11 items-center rounded-full border border-surface-700 bg-surface-800 px-4 text-sm text-surface-200 transition-colors hover:text-primary-300"
              >{{ o.name }}</RouterLink>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <section class="px-6 py-14 text-center sm:px-8 lg:px-10">
      <a
        :href="ctaHref"
        class="inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-primary-600 px-7 py-3.5 text-sm font-semibold text-white shadow-lg transition-colors hover:bg-primary-700 sm:w-auto"
      >
        Try {{ model.name }}
        <ArrowRight class="h-4 w-4" />
      </a>
    </section>
  </div>
</template>
